import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Box,
  Typography,
  Card,
  CardMedia,
  Grid,
  Pagination,
  InputAdornment,
  IconButton,
  CircularProgress,
  Button,
  FormControlLabel,
  Switch,
  Chip,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SortIcon from '@mui/icons-material/Sort';
import BaseDialog from './BaseDialog';
import { searchGames } from '../../services/contents_api';

export interface GameSelectResult {
  game_id: number;
  name: string;
  japanese_name?: string | null;
  first_release_date?: string | null;
  cover?: string | null;
}

interface GameSelectDialogProps {
  open: boolean;
  onClose: () => void;
  /** 単一選択時のコールバック */
  onSelect?: (game: GameSelectResult) => void;
  /** 複数選択時のコールバック */
  onMultiSelect?: (games: GameSelectResult[]) => void;
  title?: string;
  excludeGameIds?: number[];
  /** 全検索トグルを表示するか（デフォルト: false = ライブラリのみ） */
  showAllSearchOption?: boolean;
  /** 複数選択を有効にするか（デフォルト: false = 単一選択） */
  multiSelect?: boolean;
  /** 複数選択時の最大選択数（未指定で無制限） */
  maxSelect?: number;
  /**
   * 最初から全ゲーム検索で開くか（デフォルト: false = ライブラリから開く）。
   * ライブラリという概念がない用途（管理画面のニュース紐づけ等）で使う。
   * showAllSearchOption と併用すればライブラリ側へ切り替えることもできる。
   */
  defaultAllSearch?: boolean;
}

const statusOptions = [
  { value: 1, label: 'プレイ済み' },
  { value: 2, label: 'プレイ中' },
  { value: 3, label: '中断' },
  { value: 4, label: '積みゲー' },
  { value: 5, label: 'ウィッシュ' },
];

const defaultStatuses = [1, 2, 3];

const orderOptions = [
  { value: 'default', label: 'デフォルト' },
  { value: 'rating_desc', label: '評価 (高い順)' },
  { value: 'rating', label: '評価 (低い順)' },
  { value: 'play_time_desc', label: 'プレイ時間 (長い順)' },
  { value: 'play_time', label: 'プレイ時間 (短い順)' },
  { value: 'play_start_date_desc', label: 'プレイ開始日 (新しい順)' },
  { value: 'play_start_date', label: 'プレイ開始日 (古い順)' },
  { value: 'release_date_desc', label: '発売日 (新しい順)' },
  { value: 'release_date', label: '発売日 (古い順)' },
  { value: 'added_date_desc', label: '追加日 (新しい順)' },
  { value: 'added_date', label: '追加日 (古い順)' },
];

const GameSelectDialog: React.FC<GameSelectDialogProps> = ({
  open,
  onClose,
  onSelect,
  onMultiSelect,
  title = 'ゲームを選択',
  excludeGameIds = [],
  showAllSearchOption = false,
  multiSelect = false,
  maxSelect,
  defaultAllSearch = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [games, setGames] = useState<GameSelectResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [inLibrary, setInLibrary] = useState(!defaultAllSearch);
  const [selectedGames, setSelectedGames] = useState<GameSelectResult[]>([]);
  const [order, setOrder] = useState('default');
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>(defaultStatuses);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  useEffect(() => {
    if (open) {
      // 開くたびに初期状態へ戻す。ただし defaultAllSearch を指定していれば
      // ライブラリではなく全ゲーム検索から始める（管理画面など、
      // 開いた本人のライブラリとは関係ない用途のため）
      const startInLibrary = !defaultAllSearch;
      setSearchQuery('');
      setPage(1);
      setInLibrary(startInLibrary);
      setSelectedGames([]);
      setOrder('default');
      setSelectedStatuses(defaultStatuses);
      fetchGames('', 1, startInLibrary, 'default', defaultStatuses);
    }
  }, [open, defaultAllSearch]);

  const fetchGames = async (
    query: string,
    pageNum: number,
    libraryOnly: boolean = inLibrary,
    sortOrder: string = order,
    statuses: number[] = selectedStatuses
  ) => {
    setLoading(true);
    try {
      const response = await searchGames(query, libraryOnly, pageNum, excludeGameIds, sortOrder, statuses);
      setGames(response.results);
      setTotalPages(Math.ceil(response.count / response.page_size));
    } catch (err) {
      console.error('Failed to fetch games:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchGames(searchQuery, 1);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(1);
    fetchGames('', 1);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    fetchGames(searchQuery, value);
  };

  const handleLibraryToggle = (checked: boolean) => {
    setInLibrary(checked);
    setPage(1);
    if (!checked) {
      setOrder('default');
      fetchGames(searchQuery, 1, checked, 'default', selectedStatuses);
    } else {
      fetchGames(searchQuery, 1, checked, order, selectedStatuses);
    }
  };

  const handleStatusToggle = (statusValue: number) => {
    const newStatuses = selectedStatuses.includes(statusValue)
      ? selectedStatuses.filter(s => s !== statusValue)
      : [...selectedStatuses, statusValue].sort();
    if (newStatuses.length === 0) return;
    setSelectedStatuses(newStatuses);
    setPage(1);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchGames(searchQuery, 1, inLibrary, order, newStatuses);
    }, 500);
  };

  const handleOrderChange = (event: SelectChangeEvent) => {
    const newOrder = event.target.value;
    setOrder(newOrder);
    setPage(1);
    fetchGames(searchQuery, 1, inLibrary, newOrder);
  };

  const handleGameClick = (game: GameSelectResult) => {
    if (multiSelect) {
      setSelectedGames(prev => {
        const exists = prev.some(g => g.game_id === game.game_id);
        if (exists) {
          return prev.filter(g => g.game_id !== game.game_id);
        }
        if (maxSelect !== undefined && prev.length >= maxSelect) {
          return prev;
        }
        return [...prev, game];
      });
    } else {
      onSelect?.(game);
      onClose();
    }
  };

  const handleConfirmMultiSelect = () => {
    onMultiSelect?.(selectedGames);
    onClose();
  };

  const isSelected = (gameId: number) =>
    selectedGames.some(g => g.game_id === gameId);

  const actions = multiSelect ? (
    <>
      <Button onClick={onClose} size="small">
        キャンセル
      </Button>
      <Button
        onClick={handleConfirmMultiSelect}
        variant="contained"
        size="small"
        disabled={selectedGames.length === 0}
      >
        {selectedGames.length > 0
          ? maxSelect !== undefined
            ? `${selectedGames.length}/${maxSelect}件を追加`
            : `${selectedGames.length}件を追加`
          : '追加'}
      </Button>
    </>
  ) : undefined;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="md"
      actions={actions}
    >
      <Box sx={{ minHeight: '85vh' }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {inLibrary ? 'ライブラリからゲームを選択' : 'すべてのゲームから検索'}
        {multiSelect && maxSelect !== undefined
          ? ` (${selectedGames.length}/${maxSelect}件選択中)`
          : multiSelect && selectedGames.length > 0
            ? ` (${selectedGames.length}件選択中)`
            : ''}
      </Typography>

      {/* 検索バー + ソート */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            placeholder={inLibrary ? 'ライブラリ内を検索...' : 'すべてのゲームを検索...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {inLibrary && (
            <Select
              value={order}
              onChange={handleOrderChange}
              size="small"
              sx={{
                minWidth: { xs: 80, sm: 180 },
                height: 32,
                fontSize: '0.75rem',
                backgroundColor: 'rgba(255, 255, 255, 0.06)',
                borderRadius: '4px',
                '& .MuiSelect-select': {
                  paddingY: 0,
                  paddingX: 1,
                  display: 'flex',
                  alignItems: 'center',
                  color: 'text.secondary',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                },
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                  <SortIcon sx={{ mr: 0.5, fontSize: '1rem', flexShrink: 0 }} />
                  <Box
                    component="span"
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {orderOptions.find(o => o.value === selected)?.label || '並び替え'}
                  </Box>
                </Box>
              )}
            >
              {orderOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value} dense>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>
        {inLibrary && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {statusOptions.map(opt => (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                variant={selectedStatuses.includes(opt.value) ? 'filled' : 'outlined'}
                color={selectedStatuses.includes(opt.value) ? 'primary' : 'default'}
                onClick={() => handleStatusToggle(opt.value)}
                sx={{ fontSize: '0.7rem', height: 24 }}
              />
            ))}
          </Box>
        )}
        {showAllSearchOption && (
          <FormControlLabel
            control={
              <Switch
                checked={!inLibrary}
                onChange={(e) => handleLibraryToggle(!e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                ライブラリ以外も含めて検索
              </Typography>
            }
            sx={{ mt: 1 }}
          />
        )}
      </Box>

      {/* 複数選択時の選択済みチップ */}
      {multiSelect && selectedGames.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selectedGames.map(game => (
            <Chip
              key={game.game_id}
              label={game.japanese_name || game.name}
              size="small"
              onDelete={() => setSelectedGames(prev => prev.filter(g => g.game_id !== game.game_id))}
            />
          ))}
        </Box>
      )}

      {/* ゲーム一覧 */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : games.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {!inLibrary && searchQuery.length < 2
              ? '2文字以上で検索してください'
              : 'ゲームが見つかりませんでした'}
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={1}>
            {games.map((game) => (
              <Grid item xs={4} sm={3} md={2} key={game.game_id}>
                <Card
                  onClick={() => handleGameClick(game)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    border: isSelected(game.game_id) ? '2px solid' : '1px solid transparent',
                    borderColor: isSelected(game.game_id) ? 'primary.main' : 'transparent',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3,
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    image={game.cover || '/image/no_image.png'}
                    alt={game.japanese_name || game.name}
                    sx={{ aspectRatio: '3/4', objectFit: 'cover' }}
                  />
                  {multiSelect && isSelected(game.game_id) && (
                    <CheckCircleIcon
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        color: 'primary.main',
                        backgroundColor: 'background.paper',
                        borderRadius: '50%',
                        fontSize: 22,
                      }}
                    />
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
      </Box>
    </BaseDialog>
  );
};

export default GameSelectDialog;
